import { isSymbol } from './strings';

export const getFirstElement = (arr: any[]) => {
  if (!Array.isArray(arr) || !arr.length) return undefined;
  return arr[0];
};

export const getLastElement = (arr: any[]) => {
  if (!Array.isArray(arr) || !arr.length) return undefined;
  return arr[arr.length - 1];
};

/**
 * Creates a sorting function that handles alphanumeric sorting with special character handling.
 * The sorting follows these rules:
 * 1. Empty values are placed at the end
 * 2. Special characters (non-alphanumeric) are sorted first
 * 3. Numbers are sorted numerically
 * 4. Strings are sorted alphabetically (case-insensitive)
 *
 * @param key - The object key to sort by. Can be a nested key using dot notation (e.g., 'user.name')
 * @returns A sorting function that can be used with Array.sort()
 *
 * @example
 * // Basic usage
 * const arr = [{ name: 'Bob' }, { name: 'Alice' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'Alice' }, { name: 'Bob' }]
 *
 * @example
 * // Special characters come first
 * const arr = [{ name: 'Bob' }, { name: '@Alice' }, { name: 'Charlie' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: '@Alice' }, { name: 'Bob' }, { name: 'Charlie' }]
 *
 * @example
 * // Numbers are sorted numerically
 * const arr = [{ name: 'User 10' }, { name: 'User 2' }, { name: 'User 1' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'User 1' }, { name: 'User 2' }, { name: 'User 10' }]
 *
 * @example
 * // Case insensitive sorting
 * const arr = [{ name: 'bob' }, { name: 'Alice' }, { name: 'Charlie' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'Alice' }, { name: 'bob' }, { name: 'Charlie' }]
 *
 * @example
 * // Nested object sorting
 * const arr = [{ user: { name: 'Bob' } }, { user: { name: 'Alice' } }];
 * arr.sort(sortAlphaNumerically('user.name'));
 * // Result: [{ user: { name: 'Alice' } }, { user: { name: 'Bob' } }]
 */
export const sortAlphaNumerically =
  (key: string) =>
  (a: { [key: string]: any }, b: { [key: string]: any }): number => {
    const getNestedValue = (obj: { [key: string]: any }, path: string) => {
      if (!path.includes('.')) return obj[path] ?? '';

      return (
        path.split('.').reduce((value, splitKey) => {
          if (value === undefined || value === null) return undefined;
          return value[splitKey];
        }, obj) ?? ''
      );
    };

    const valueA = getNestedValue(a, key);
    const valueB = getNestedValue(b, key);

    // Place empty values at the end
    if (valueA === '') return 1;
    if (valueB === '') return -1;

    const startsWithSymbolA = isSymbol(valueA[0]);
    const startsWithSymbolB = isSymbol(valueB[0]);

    if (startsWithSymbolA && startsWithSymbolB) return valueA.localeCompare(valueB);
    if (startsWithSymbolA) return -1;
    if (startsWithSymbolB) return 1;

    const isNumericA = /^\d+$/.test(valueA);
    const isNumericB = /^\d+$/.test(valueB);

    if (isNumericA && isNumericB) return parseInt(valueA, 10) - parseInt(valueB, 10);
    if (isNumericA) return -1;
    if (isNumericB) return 1;

    return valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' });
  };

export const sumBy = (arr: Record<string, any>[], key: string) => {
  if (!arr || !Array.isArray(arr) || !key) return 0;

  return (arr || []).reduce((acc, curr) => {
    if (!key.includes('.')) {
      if (typeof curr[key] !== 'number') return acc;
      return acc + curr[key];
    }

    const nestedKeys = key.split('.');
    let nestedValue = curr;
    let num = 0;
    for (let i = 0; i < nestedKeys.length; i++) {
      if (i === nestedKeys.length - 1) {
        num = nestedValue[nestedKeys[i]] as number;
      } else {
        nestedValue = nestedValue[nestedKeys[i]];
      }
    }
    return acc + num;
  }, 0);
};
